// src/context/DocumentsView.jsx
//
// Reusable RAG-documents corpus body, keyed on an explicit tenantId (mirrors the
// DashboardView pattern). Mounted through KnowledgeBaseView three ways:
//   • tenant-admin console  → TenantKnowledgeBasePage  (canWrite=true when admin)
//   • superadmin console    → admin/KnowledgeBasePage  (tenant dropdown, canWrite)
//   • member read-only      → sections/KnowledgeBase   (canWrite=isTenantAdmin)
//
// canWrite gates upload/reindex/delete; the BE 403 is a backstop. DOWNLOAD is a
// read and is offered to every member.
//
// Contract facts this file encodes (<BACKEND_FOLDER>/docs/frontend/api-contract.md, "Context
// Documents"):
//   • upload and reindex are CREDIT-GATED (402 / 503 LOOKUP_FAILED) — worded
//     through RunRefusalNotice like a run refusal, never as the server's
//     English; delete and download are never gated;
//   • uploading an existing filename REPLACES it silently (no 409) — the
//     confirm is ours;
//   • ONE file per request — a multi-select pick issues N sequential requests
//     with per-file outcomes, and stops at the first refusal (the wallet is
//     shared: the rest would refuse too);
//   • a `ready` document may carry chunk_count 0 (scanned PDF, text layer only,
//     no OCR) — badged on the row, because `ready` does not mean usable;
//   • `stale` means NOT INDEXED YET, never "out of date" (label in context.json);
//   • the list is whole (no server paging) — paginated client-side.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Trash2, RefreshCw, Upload, FileText, AlertTriangle } from 'lucide-react';
import i18n from '../i18n/index.js';
import AdminTable from '../admin/components/AdminTable.jsx';
import AdminPagination from '../admin/components/AdminPagination.jsx';
import ConfirmDialog from '../admin/components/ConfirmDialog.jsx';
import DocumentStatusBadge from './DocumentStatusBadge.jsx';
import RunRefusalNotice from '../components/credits/RunRefusalNotice.jsx';
import { refusalFromError } from '../utils/runRefusal.js';
import { contextErrorCopy, knownContextCopy } from './contextErrorCopy.js';
import { triggerBlobDownload } from '../utils/blobDownload.js';
import {
  listDocuments, uploadDocument, reindexDocument, deleteDocument, downloadDocument,
} from './contextApi.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../constants/theme.js';
import { ACCEPT, ALLOWED_EXT, namesToReplace, uploadSequentially } from './documentUpload.js';

const FONT = FONT_SANS;
const ACCENT = COLORS.accent;
const PAGE_SIZE = 10;
const POLL_START_INTERVAL = 2000; // first poll after 2s
const POLL_MAX_INTERVAL = 10000;  // backoff cap
const POLL_BACKOFF = 1.5;         // exponential factor
const POLL_TIMEOUT = 60000;       // stop auto-polling after ~60s (non-fatal)

function formatBytes(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
}

// The helper answers a KEY; the sentence is this component's (same copy as before).
function problemMessage(problem) {
  return i18n.t(`context:documents.validation.${problem.key}`, problem.params);
}

// ⛔ Every refusal is worded from its code (contextErrorCopy) — never `err.message`, which carries the
// server's English for any code parseApiError does not know. `generic` is the action's own sentence.
const copyKeys = (generic) => ({ server: 'fallback.server', network: 'fallback.network', generic });

export default function DocumentsView({ tenantId, canWrite = false }) {
  const { t } = useTranslation('context');
  // load() words its refusal with the CURRENT t, without depending on it: a
  // language switch must not reset the page and re-fetch the list.
  const tRef = useRef(t);
  tRef.current = t;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [refusal, setRefusal] = useState(null);   // a 402/503 verdict on upload or reindex
  const [outcomes, setOutcomes] = useState([]);   // per-file results of a multi-file pick
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmReplace, setConfirmReplace] = useState(null); // { files, names }

  const fileInputRef = useRef(null);
  const pollTimerRef = useRef(null);
  const pollStartRef = useRef(0);
  const pollDelayRef = useRef(POLL_START_INTERVAL);

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const fetchList = useCallback(async () => {
    const data = await listDocuments({ tenant: tenantId });
    setDocs(data.documents || []);
    return data.documents || [];
  }, [tenantId]);

  const load = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      await fetchList();
    } catch (e) {
      setError(contextErrorCopy(tRef.current, e, copyKeys('documents.errors.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [tenantId, fetchList]);

  useEffect(() => {
    clearPoll();
    setOffset(0);
    setOutcomes([]);
    setRefusal(null);
    load();
    return clearPoll;
  }, [load, clearPoll]);

  // Keep the page in range when rows disappear (delete, a refetch that dropped one).
  useEffect(() => {
    if (offset > 0 && offset >= docs.length) {
      setOffset(Math.max(0, Math.floor((docs.length - 1) / PAGE_SIZE) * PAGE_SIZE));
    }
  }, [docs.length, offset]);

  const poll = useCallback(async () => {
    let list = null; // null = this poll's fetch failed (transient)
    try {
      list = await fetchList();
    } catch {
      // Transient error — do NOT evaluate an empty list as "done indexing"
      // (that would silently kill auto-polling). Keep polling until the time
      // window closes; the last known list stays on screen via fetchList's
      // own setDocs (which is skipped on throw, so prior rows persist).
    }
    const elapsed = Date.now() - pollStartRef.current;
    const stillIndexing = list === null || list.some(d => d.status === 'indexing');
    if (stillIndexing && elapsed < POLL_TIMEOUT) {
      pollDelayRef.current = Math.min(pollDelayRef.current * POLL_BACKOFF, POLL_MAX_INTERVAL);
      pollTimerRef.current = setTimeout(poll, pollDelayRef.current);
    } else if (stillIndexing) {
      // Non-fatal: a large file can index past the window. Stop auto-polling
      // and let the user refresh manually — NOT an error state.
      setNotice(t('documents.stillIndexing'));
    } else {
      setNotice('');
    }
  }, [fetchList, t]);

  const startPolling = useCallback(() => {
    clearPoll();
    pollStartRef.current = Date.now();
    pollDelayRef.current = POLL_START_INTERVAL;
    pollTimerRef.current = setTimeout(poll, pollDelayRef.current);
  }, [poll, clearPoll]);

  // ── upload ───────────────────────────────────────────────────────────────

  async function runUploads(files) {
    setError('');
    setNotice('');
    setRefusal(null);
    setOutcomes([]);
    setBusy(true);
    // The rows as they were before this batch: a failed REPLACE must put the
    // original row back, since the optimistic row displaced it (the server
    // still holds the old file — only the table had forgotten it).
    const before = docs;
    const raw = await uploadSequentially({
      files,
      upload: (file) => uploadDocument({ tenant: tenantId, file }),
      isRefusal: refusalFromError,
      onStart: (file) => setDocs(prev => [
        { filename: file.name, size: file.size, status: 'indexing', chunk_count: null, indexed_at: null, _optimistic: true },
        ...prev.filter(d => d.filename !== file.name),
      ]),
      onFailure: (file, err, verdict) => {
        setDocs(prev => {
          const original = before.find(d => d.filename === file.name && !d._optimistic) || null;
          return prev
            .map(d => (d._optimistic && d.filename === file.name ? original : d))
            .filter(Boolean);
        });
        // The wallet is shared across the customer's workspaces: once one
        // upload is refused, every later one would be too — the helper stops.
        if (verdict) setRefusal(verdict);
      },
    });
    const results = raw.map((r) => {
      const filename = r.file.name;
      if (r.skipped) return { filename, ok: false, message: t('documents.outcomes.skipped') };
      if (r.problem) return { filename, ok: false, message: problemMessage(r.problem) };
      if (r.ok) return { filename, ok: true };
      if (r.refusal) return { filename, ok: false, message: null };
      return { filename, ok: false, message: contextErrorCopy(t, r.error, copyKeys('documents.errors.uploadFailed')) };
    });
    setBusy(false);
    if (files.length === 1) {
      const [only] = results;
      if (!only.ok && only.message) setError(only.message);
    } else {
      setOutcomes(results);
    }
    if (results.some(r => r.ok)) startPolling();
  }

  function enqueue(files) {
    if (!files.length || !canWrite) return;
    const names = namesToReplace(files, docs);
    if (names.length) {
      setConfirmReplace({ files, names });
      return;
    }
    runUploads(files);
  }

  function handleFilesPicked(e) {
    const files = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    enqueue(files);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    if (busy) return;
    enqueue(Array.from(e.dataTransfer?.files || []));
  }

  // ── row actions ──────────────────────────────────────────────────────────

  async function handleReindex(filename) {
    setError('');
    setNotice('');
    setRefusal(null);
    setBusy(true);
    setDocs(prev => prev.map(d => (d.filename === filename ? { ...d, status: 'indexing' } : d)));
    try {
      await reindexDocument({ tenant: tenantId, filename });
      startPolling();
    } catch (err) {
      const verdict = refusalFromError(err);
      // The re-read first: load() clears `error`, so a refusal set before it was
      // wiped before anyone could read it.
      await load();
      if (verdict) setRefusal(verdict);
      else setError(contextErrorCopy(t, err, copyKeys('documents.errors.reindexFailed')));
    } finally {
      setBusy(false);
    }
  }

  async function handleDownload(filename) {
    setError('');
    try {
      const blob = await downloadDocument({ tenant: tenantId, filename });
      triggerBlobDownload(blob, filename);
    } catch (err) {
      // A known code keeps «Download non riuscito: <its reason>»; anything else prints the code.
      const known = knownContextCopy(t, err);
      setError(known
        ? t('documents.downloadFailed', { message: known })
        : contextErrorCopy(t, err, copyKeys('documents.errors.downloadFailed')));
    }
  }

  async function handleDelete(filename) {
    // thrown errors are surfaced by ConfirmDialog's own error slot
    try {
      await deleteDocument({ tenant: tenantId, filename });
    } catch (err) {
      // Already gone on the BE (404) → treat as success and drop the stale
      // row; anything else is worded HERE and rethrown, because ConfirmDialog
      // renders whatever it is handed as its sentence.
      if (err.code !== 'DOCUMENT_NOT_FOUND') {
        throw new Error(contextErrorCopy(t, err, copyKeys('documents.errors.deleteFailed')));
      }
    }
    setDocs(prev => prev.filter(d => d.filename !== filename));
  }

  // ── table ────────────────────────────────────────────────────────────────

  const columns = [
    {
      key: 'filename', label: t('documents.columns.filename'),
      render: row => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontWeight: 500, wordBreak: 'break-all' }}>
            <FileText size={14} style={{ color: COLORS.textSecondary, flexShrink: 0 }} />
            {row.filename}
          </span>
          <span style={{ fontSize: '11px', color: COLORS.textTertiary, paddingLeft: '22px' }}>
            {formatBytes(row.size)}
          </span>
          {row.status === 'failed' && row.error && (
            <span style={{ fontSize: '11px', color: SEMANTIC.errore.fg, paddingLeft: '22px' }}>{row.error}</span>
          )}
        </div>
      ),
    },
    {
      key: 'status', label: t('documents.columns.status'), width: '190px',
      render: row => (
        <span style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>
          <DocumentStatusBadge status={row.status} />
          {row.status === 'ready' && row.chunk_count === 0 && (
            <span title={t('documents.noTextHint')} style={pill(SEMANTIC.attenzione)}>
              {t('documents.noText')}
            </span>
          )}
        </span>
      ),
    },
    { key: 'indexed_at', label: t('documents.columns.indexed'), width: '150px', render: row => formatDate(row.indexed_at) },
    {
      key: 'actions', label: '', width: canWrite ? '150px' : '60px',
      render: row => (
        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            aria-label={t('documents.download')}
            title={t('documents.download')}
            disabled={row._optimistic}
            onClick={() => handleDownload(row.filename)}
            style={iconBtn(!!row._optimistic)}
          >
            <Download size={15} />
          </button>
          {canWrite && (row.status === 'failed' || row.status === 'stale') && (
            <button
              type="button"
              aria-label={t('documents.reindex')}
              title={t('documents.reindex')}
              disabled={busy}
              onClick={() => handleReindex(row.filename)}
              style={iconBtn(busy)}
            >
              <RefreshCw size={15} />
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              aria-label={t('documents.delete')}
              title={t('documents.delete')}
              disabled={busy || row._optimistic}
              onClick={() => setConfirmDelete(row.filename)}
              style={{ ...iconBtn(busy || row._optimistic), background: SEMANTIC.errore.fg, borderColor: SEMANTIC.errore.fg, color: COLORS.surface }}
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      ),
    },
  ];

  if (!tenantId) {
    return <div style={emptyBox}>{t('noTenant')}</div>;
  }

  const pageRows = docs.slice(offset, offset + PAGE_SIZE);

  return (
    <div>
      {refusal && (
        <div style={{ marginBottom: '16px' }}>
          <RunRefusalNotice refusal={refusal} onDismiss={() => setRefusal(null)} />
        </div>
      )}
      {error && <div role="alert" style={alertBox(SEMANTIC.errore.fg)}>{error}</div>}
      {notice && <div style={alertBox(SEMANTIC.attenzione.fg)}>{notice}</div>}

      {canWrite && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT}
            onChange={handleFilesPicked}
            style={{ display: 'none' }}
          />
          <div
            role="button"
            tabIndex={0}
            aria-label={t('documents.dropTitle')}
            onClick={() => { if (!busy) fileInputRef.current?.click(); }}
            onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && !busy) { e.preventDefault(); fileInputRef.current?.click(); } }}
            onDragOver={e => { e.preventDefault(); if (!dragging) setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            style={dropZone(dragging, busy)}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: 600, fontSize: '13px', color: COLORS.ink }}>
              <Upload size={16} />
              {busy ? t('documents.uploading') : t('documents.dropTitle')}
            </div>
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: COLORS.textSecondary, maxWidth: '520px', marginLeft: 'auto', marginRight: 'auto' }}>
              {t('documents.dropHint')}
            </p>
            <div style={warnStrip}>
              <AlertTriangle size={14} />
              {t('documents.sensitiveWarning')}
            </div>
            <p style={{ margin: '10px 0 0', fontSize: '11px', color: COLORS.textTertiary }}>
              {t('documents.allowedFormats', { exts: ALLOWED_EXT.join(', ').toUpperCase() })}
            </p>
          </div>
        </>
      )}

      {outcomes.length > 0 && (
        <div style={outcomesBox}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <strong style={{ fontSize: '12px' }}>{t('documents.outcomes.title')}</strong>
            <button type="button" onClick={() => setOutcomes([])} style={ghostBtn(false)}>{t('documents.outcomes.dismiss')}</button>
          </div>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', lineHeight: 1.6 }}>
            {outcomes.map(o => (
              <li key={o.filename} style={{ color: o.ok ? COLORS.ink : SEMANTIC.errore.fg }}>
                <span style={{ fontWeight: 500 }}>{o.filename}</span>
                {' — '}
                {o.ok ? t('documents.outcomes.ok') : (o.message || t('documents.outcomes.failed'))}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <button
          type="button"
          onClick={() => { load(); if (docs.some(d => d.status === 'indexing')) startPolling(); }}
          disabled={loading}
          style={ghostBtn(loading)}
        >
          {t('documents.refresh')}
        </button>
      </div>

      <div style={{ background: COLORS.surface, borderRadius: RADII.control, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
        <AdminTable
          columns={columns}
          rows={pageRows}
          loading={loading}
          emptyMessage={t('documents.empty')}
          getRowKey={row => row.filename}
        />
      </div>
      <AdminPagination
        total={docs.length}
        limit={PAGE_SIZE}
        offset={offset}
        onChange={setOffset}
        itemLabel={t('documents.itemLabel')}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => handleDelete(confirmDelete)}
        title={t('documents.confirmDelete.title')}
        message={t('documents.confirmDelete.message', { filename: confirmDelete })}
        confirmLabel={t('documents.delete')}
        variant="danger"
      />

      <ConfirmDialog
        open={!!confirmReplace}
        onClose={() => setConfirmReplace(null)}
        onConfirm={() => { const { files } = confirmReplace; runUploads(files); }}
        title={t('documents.confirmReplace.title')}
        message={t('documents.confirmReplace.message', { names: (confirmReplace?.names || []).join('", "') })}
        confirmLabel={t('documents.confirmReplace.confirm')}
        variant="danger"
      />
    </div>
  );
}

// ── inline styles ───────────────────────────────────────────────────────────
const emptyBox = {
  padding: '60px 40px', textAlign: 'center', background: COLORS.surface,
  borderRadius: RADII.control, border: `1px solid ${COLORS.border}`,
  color: COLORS.textSecondary, fontSize: '13px', fontFamily: FONT,
};

const outcomesBox = {
  marginBottom: '16px', padding: '10px 14px', borderRadius: RADII.controlInner,
  background: COLORS.field, border: `1px solid ${COLORS.border}`, fontFamily: FONT,
  color: COLORS.ink,
};

const warnStrip = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
  marginTop: '14px', padding: '8px 12px', borderRadius: RADII.controlInner,
  background: SEMANTIC.attenzione.bg, color: SEMANTIC.attenzione.fg, fontSize: '12px',
};

function dropZone(dragging, disabled) {
  return {
    marginBottom: '16px', padding: '20px 24px', textAlign: 'center',
    borderRadius: RADII.control, fontFamily: FONT,
    border: `1.5px dashed ${dragging ? ACCENT : COLORS.borderStrong}`,
    background: dragging ? COLORS.accentSoft : COLORS.surface,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.7 : 1,
    outline: 'none',
  };
}

function pill(c) {
  return {
    padding: '2px 8px', borderRadius: RADII.controlInner, fontSize: '11px', fontWeight: 600,
    background: c.bg, color: c.fg, fontFamily: FONT, whiteSpace: 'nowrap',
  };
}

function alertBox(color) {
  return {
    marginBottom: '16px', padding: '10px 14px', borderRadius: RADII.controlInner,
    background: `${color}14`, border: `1px solid ${color}33`,
    color, fontSize: '13px', fontFamily: FONT,
  };
}

function ghostBtn(disabled) {
  return {
    padding: '6px 12px', borderRadius: RADII.controlInner,
    border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
    color: COLORS.ink, fontSize: '12px', fontFamily: FONT,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  };
}

function iconBtn(disabled) {
  return {
    width: '34px', height: '34px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: RADII.controlInner, border: `1px solid ${COLORS.borderStrong}`,
    background: COLORS.surface, color: COLORS.ink,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
  };
}
